// Pricing engine — DB-agnostic dimensional type contract.
//
// Pure Zod schemas + inferred types for the deterministic price-resolution
// engine. No DB handles, no React, no Date.now() — these are the data shapes
// the engine's pure functions consume and produce. The `ProductPrice` type is
// defined fresh here (mappable from the `product_prices` table by future
// wiring; the engine treats product_prices, not pricing_dimension_adders, as
// the eventual source of truth). The composition child publishes this module
// through the features/pricing barrel; downstream children import by relative
// path. Authoritative design: planning/pricing-model-proposal.md §1–2 / §5.1,
// refined by the SPEC-SOR-297 glossary.
//
// Enum string values are kebab-case (codebase convention — cf. 'court-filing',
// 'on-demand', 'not-linked'). The spec's snake_case is a formal-FOL grammar
// artifact and does NOT dictate the Zod literals.
import { z } from 'zod/v4';
import { CustomPricingRuleTypeSchema } from '../../setup/custom-pricing.schema.js';
// ── Dimensional enums ──
// Court-system zone — resolved from a court-location lookup (deferred to wiring).
export const CourtZoneSchema = z.enum([
    'urban-court',
    'suburban-court',
    'remote-court',
    'nationwide-court',
    'efiling-california',
]);
// Area-system zone — resolved from a zip/area lookup (deferred to wiring).
export const AreaZoneSchema = z.enum([
    'urban-area',
    'suburban-area',
    'remote-area',
    'nationwide-area',
]);
// The full pricing-zone space — a court zone or an area zone.
export const PricingZoneSchema = z.union([CourtZoneSchema, AreaZoneSchema]);
// Which zone space a product is priced in (the spec's zoneSystem discriminator):
// court-system uses a court-zone lookup, area-system a zip/area lookup, and
// flat-system has no zone (flat/ancillary products).
export const ZoneSystemSchema = z.enum([
    'court-system',
    'area-system',
    'flat-system',
]);
// Page-count size tier. `not-sized` is the explicit member for size-independent
// products — the spec (R2) folds the proposal's null-tier into an enum value so
// the tiers are total and disjoint over the page-count domain.
export const SizeTierSchema = z.enum([
    'small', // 1–25 pages
    'medium', // 26–75 pages
    'large', // 76+ pages
    'not-sized', // size-independent product
]);
// Urgency / speed tier.
export const ServiceLevelSchema = z.enum([
    'standard',
    'priority',
    'urgent',
    'on-demand',
    'same-day',
    'next-day',
    '2nd-day',
]);
// Whether a courtesy-copy row is standalone or bundled with a same-court filing.
export const BundleContextSchema = z.enum(['standalone', 'with-filing']);
// Account client type. `all` is the price-row wildcard; the resolver (R3)
// returns only `law-firm` / `non-law-firm`.
export const ClientTypeSchema = z.enum(['all', 'law-firm', 'non-law-firm']);
// Per-unit billing basis for per-unit price rows (null perUnit = flat fee).
export const PerUnitTypeSchema = z.enum(['page', 'half-hour', 'attempt', 'mile']);
// Itemized line-item kind. The aggregate-bucket partition (composition child):
// base/per-unit/surcharge → subtotal, discount → custom, processing-fee → fee.
export const LineItemTypeSchema = z.enum([
    'base',
    'per-unit',
    'discount',
    'surcharge',
    'processing-fee',
]);
// Fee class for processing-fee-base gating. statutory/efm/service mirror the
// financial proposal's pass-through fee classes; `none` covers line items that
// belong to no fee base.
export const FeeClassSchema = z.enum(['statutory', 'efm', 'service', 'none']);
// ── Object types ──
// DB-agnostic product price row. Defined fresh; mappable from the
// `product_prices` table by future wiring. `price` can be negative (bundle
// discounts). A null dimension means the row applies regardless of that
// dimension (e.g. null zone for flat/ancillary rows).
export const ProductPriceSchema = z.object({
    id: z.string().uuid(),
    productId: z.string().uuid(),
    zone: PricingZoneSchema.nullable(),
    sizeTier: SizeTierSchema.nullable(),
    serviceLevel: ServiceLevelSchema.nullable(),
    bundleContext: BundleContextSchema.default('standalone'),
    clientType: ClientTypeSchema.default('all'),
    price: z.number(),
    perUnit: PerUnitTypeSchema.nullable(),
    unitThreshold: z.number().nullable(),
    effectiveDate: z.string().datetime(),
    expirationDate: z.string().datetime().nullable(),
    sortOrder: z.number().default(0),
});
// DB-agnostic custom-pricing overlay rule. Defined fresh; mappable from the
// `custom_pricing` table by future wiring (the same pattern as `ProductPrice`).
// `priority` orders the rules — higher wins among non-stackable competitors
// (R6); `stackable` decides whether the rule combines with others (R7) or
// competes for the single non-stackable slot (R6/R8). The match criteria mirror
// `PricingEntry`: a rule matches an item by product / category / pricing-item id
// (or `applyToAllProducts`), gated by an optional [minQuantity, maxQuantity]
// band. `ruleType` reuses the master record's custom-pricing rule vocabulary.
export const CustomRuleSchema = z.object({
    id: z.string(),
    priority: z.number(),
    stackable: z.boolean(),
    ruleType: CustomPricingRuleTypeSchema,
    value: z.number(),
    productId: z.string().uuid().optional(),
    productCategory: z.string().optional(),
    pricingItemId: z.string().uuid().optional(),
    applyToAllProducts: z.boolean().default(false),
    minQuantity: z.number().optional(),
    maxQuantity: z.number().optional(),
});
// Inputs to a single-item price calculation. `effectiveDate` is an explicit
// parameter (spec R12 — the engine is pure and deterministic; never Date.now()).
export const PriceCalculationInputSchema = z.object({
    productId: z.string().uuid(),
    courtLocationId: z.string().uuid().optional(), // court-system products
    serviceZipCode: z.string().optional(), // area-system products
    pageCount: z.number().int().positive().optional(), // size-tier products
    serviceLevel: ServiceLevelSchema.optional(),
    accountId: z.string().uuid(),
    bundleContext: BundleContextSchema.optional(),
    quantity: z.number().optional(), // per-unit items
    effectiveDate: z.string().datetime(),
});
// One itemized component of a breakdown. `feeClass` records which fee base the
// item belongs to (`none` = outside every base) for processing-fee gating.
export const LineItemSchema = z.object({
    label: z.string(),
    amount: z.number(),
    type: LineItemTypeSchema,
    feeClass: FeeClassSchema,
});
// The resolved price of a single item.
export const PriceBreakdownSchema = z.object({
    basePrice: z.number(),
    perUnitPrice: z.number().nullable(),
    unitQuantity: z.number().nullable(),
    unitSubtotal: z.number(),
    subtotal: z.number(),
    customPricingAdjustment: z.number(),
    processingFee: z.number(),
    total: z.number(),
    breakdown: z.array(LineItemSchema),
});
